import type { PivotLayoutMemberSort, PivotLayoutValueSort } from '@ztechapps/common.contract'
import type { PivotNode, PivotResult } from './types'
import { DirectionEnum } from '@ztechapps/common.contract'
import { findPivotLayoutItem } from './pivotLayout.utils'
import { sortPivotMembers } from './pivotMember.utils'
import { resolvePivotValueColumn } from './pivotValueColumn.utils'

const reorderMembers = (node: PivotNode, directions: (DirectionEnum | undefined)[]): PivotNode => {
  const children = node.children.map((child) => reorderMembers(child, directions))
  // a node's children are the members of field index depth, so the root's own depth reads the first field
  const direction = directions[node.depth]
  return {
    ...node,
    children: direction === DirectionEnum.desc ? sortPivotMembers(children, direction) : children,
  }
}

const reorderByValue = (
  result: PivotResult,
  root: PivotNode,
  target: { columnKey: string; measureIndex: number; direction: DirectionEnum }
): PivotNode => {
  // each child's value is read once, not once per comparison
  const sortChildren = (children: PivotNode[]): PivotNode[] =>
    children
      .map((child) => ({ child, value: result.getValue(child.key, target.columnKey, target.measureIndex) }))
      .sort((a, b) => {
        // cells with no value last in both directions; ties keep the member order (stable sort)
        if (a.value === undefined && b.value === undefined) return 0
        if (a.value === undefined) return 1
        if (b.value === undefined) return -1
        return target.direction === DirectionEnum.desc ? b.value - a.value : a.value - b.value
      })
      .map((entry) => entry.child)
  const visit = (node: PivotNode): PivotNode => ({ ...node, children: sortChildren(node.children).map(visit) })
  return visit(root)
}

/** Reorders a built pivot result by the layout's member and value sorts, keeping every value in place. */
export const sortPivotResult = ({
  result,
  memberSorts,
  valueSort,
}: {
  result: PivotResult
  memberSorts: PivotLayoutMemberSort[]
  valueSort?: PivotLayoutValueSort
}): PivotResult => {
  const rowDirections = result.rowFields.map((field) => findPivotLayoutItem(memberSorts, field)?.direction)
  const columnDirections = result.columnFields.map((field) => findPivotLayoutItem(memberSorts, field)?.direction)
  let rowRoot = result.rowRoot
  let columnRoot = result.columnRoot
  if (rowDirections.includes(DirectionEnum.desc)) rowRoot = reorderMembers(rowRoot, rowDirections)
  if (columnDirections.includes(DirectionEnum.desc)) columnRoot = reorderMembers(columnRoot, columnDirections)
  const target = valueSort ? resolvePivotValueColumn(result, valueSort) : undefined
  if (target && valueSort) rowRoot = reorderByValue(result, rowRoot, { ...target, direction: valueSort.direction })
  if (rowRoot === result.rowRoot && columnRoot === result.columnRoot) return result
  return { ...result, rowRoot, columnRoot }
}
